import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store';
import {
  useSubscriptionStatus,
  useSubscriptionPlans,
  useInitiateSubscription,
  useVerifySubscription,
  useCancelSubscription,
} from '../hooks/useMovie';
import { useAlert } from '../components/ui/AlertModal';
import toast from 'react-hot-toast';
import { Icon } from '@iconify/react';
import { getApiErrorMessage } from '../api/errors';
import { subscriptionApi } from '../api/client';
import { Button } from '../components/ui/button';
import { PageMasthead } from '../components/page-masthead';
import { cn } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';

const PERKS = [
  {
    title: '4K Ultra HD & Spatial Audio',
    description: 'Crisp picture quality with cinematic audio for every production.',
    icon: 'ph:film-slate-fill',
  },
  {
    title: 'Complete Unrestricted Access',
    description: 'Stream all movies, TV series, documentaries, and exclusives.',
    icon: 'ph:sparkle-fill',
  },
  {
    title: 'Multi-Device Support',
    description: 'Seamless viewing on your phone, tablet, laptop, and television.',
    icon: 'ph:television-fill',
  },
  {
    title: '100% Ad-Free Experience',
    description: 'No commercial interruptions, breaks, or pop-ups.',
    icon: 'ph:prohibit-fill',
  },
  {
    title: 'Instant 1-Click Cancellation',
    description: 'Zero lock-ins or contracts. Cancel any time from your account.',
    icon: 'ph:arrow-u-up-left-bold',
  },
];

export default function SubscribePage() {
  usePageTitle(
    'Subscribe & Plans · Kast',
    'Choose a Kast subscription plan. Unlimited streaming of African cinema, original series, and blockbusters in 4K Ultra HD.'
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuthStore();
  const { confirm } = useAlert();
  const [email, setEmail] = useState(user?.Email || '');
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const { data: subStatus, isLoading: statusLoading, error: statusError } = useSubscriptionStatus(!!token);
  const { data: plans = [], isLoading: plansLoading } = useSubscriptionPlans();
  const initiateMutation = useInitiateSubscription();
  const verifyMutation = useVerifySubscription();
  const cancelMutation = useCancelSubscription();

  useEffect(() => {
    if (plans.length > 0 && selectedPlanId == null) {
      setSelectedPlanId(plans[0].Id);
    }
  }, [plans, selectedPlanId]);

  useEffect(() => {
    const reference = searchParams.get('reference') || searchParams.get('trxref');
    if (reference && token) {
      toast.loading('Verifying payment…');
      verifyMutation.mutate(
        { reference },
        {
          onSuccess: () => {
            toast.dismiss();
            toast.success('Subscription activated! Enjoy unlimited streaming.');
            navigate('/');
          },
          onError: () => {
            toast.dismiss();
            toast.error('Payment verification failed. Please contact support.');
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    if (!selectedPlanId) {
      toast.error('Please choose a plan');
      return;
    }

    try {
      const { data } = await initiateMutation.mutateAsync({ email, planId: selectedPlanId });
      if (data.success) {
        window.location.href = data.data.authorizationUrl;
      } else {
        toast.error('Could not initiate payment. Please try again.');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Payment initiation failed.'));
    }
  };

  const handleCancel = async () => {
    // Ask the server what cancelling would cost this user right now (days of paid time left, and how many
    // stacked renewal periods would be cancelled) so the warning is specific, not generic.
    let warning =
      "Cancelling ends your access right away — there's no refund for the time remaining, and it can't be undone. Continue?";
    try {
      const { data } = await subscriptionApi.cancelPreview();
      if (data?.data?.warning) warning = data.data.warning;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to load cancellation details. Please try again.'));
      return;
    }

    const ok = await confirm(
      warning,
      { title: 'Cancel your subscription?', variant: 'warning', isConfirm: true, confirmLabel: 'Yes, cancel and end my access' }
    );
    if (!ok) return;
    try {
      await cancelMutation.mutateAsync();
      toast.success('Subscription cancelled.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to cancel subscription.'));
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-[75vh] flex-col items-center justify-center px-4 py-16 text-center">
        <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#120e1d] p-8 shadow-2xl">
          <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/20 text-primary">
            <Icon icon="ph:lock-key-fill" className="size-7" />
          </div>
          <h2 className="display-sm mt-6 text-2xl font-bold">Sign in to Subscribe</h2>
          <p className="mt-2.5 text-sm text-muted-foreground">
            You need a Kast account to choose a subscription plan and unlock unlimited streaming.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="brand-sweep mt-6 w-full h-11 rounded-full font-semibold text-white shadow-lg"
          >
            Sign In to Continue
          </Button>
        </div>
      </div>
    );
  }

  if (statusLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (statusError) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <p className="text-destructive">{getApiErrorMessage(statusError, 'Unable to load subscription status.')}</p>
      </div>
    );
  }

  // Active Subscriber Screen
  if (subStatus?.HasActiveSubscription) {
    return (
      <div className="min-h-[75vh] pb-24">
        <PageMasthead
          title="Active Subscription"
          lead="Your subscription is active. Stream unlimited movies and series on all your devices."
        />
        <div className="mx-auto max-w-xl gutter mt-8">
          <div className="rounded-2xl border border-white/10 bg-[#120e1d] p-8 text-center shadow-2xl backdrop-blur-md">
            <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400 ring-4 ring-emerald-500/10">
              <Icon icon="ph:check-circle-fill" className="size-9" />
            </div>

            <h2 className="display-sm mt-6 text-2xl font-bold">You're Subscribed!</h2>
            <div className="mt-4 rounded-xl border border-white/8 bg-white/5 p-4 text-sm text-foreground/90">
              {subStatus.PlanName && (
                <div className="flex justify-between py-1 border-b border-white/5">
                  <span className="text-muted-foreground">Plan</span>
                  <span className="font-semibold text-white">
                    {subStatus.PlanName} ({subStatus.BillingCycle})
                  </span>
                </div>
              )}
              {subStatus.ExpiryDate && (
                <div className="flex justify-between py-1">
                  <span className="text-muted-foreground">Active Until</span>
                  <span className="font-semibold text-white">
                    {subStatus.ExpiryDate.split('T')[0]}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <Button
                onClick={() => navigate('/')}
                size="lg"
                className="brand-sweep h-12 rounded-full font-semibold text-white shadow-lg"
              >
                Start Watching
              </Button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="text-xs text-muted-foreground hover:text-rose-400 transition underline pt-2"
              >
                {cancelMutation.isPending ? 'Cancelling…' : 'Cancel subscription'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Subscribe / Plans Selection Screen
  return (
    <div className="min-h-screen pb-24">
      <PageMasthead
        title="Choose Your Plan"
        lead="Unlock unlimited streaming of African cinema, original TV series, and blockbusters."
      />

      <div className="mx-auto max-w-[1400px] gutter mt-8">
        {/* Plans Grid */}
        {plansLoading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => {
              const perMonth =
                plan.BillingCycle === 'Annual' ? Math.round(plan.PriceNaira / 12) : plan.PriceNaira;
              const isSelected = selectedPlanId === plan.Id;
              const isAnnual = plan.BillingCycle === 'Annual';

              return (
                <button
                  key={plan.Id}
                  type="button"
                  onClick={() => setSelectedPlanId(plan.Id)}
                  className={cn(
                    'relative flex flex-col items-start rounded-2xl border p-6 text-left transition-all duration-200 cursor-pointer',
                    isSelected
                      ? 'border-primary bg-primary/10 shadow-2xl shadow-primary/20 ring-2 ring-primary/40'
                      : 'border-white/10 bg-[#120e1d] hover:border-white/20 hover:bg-white/[0.04]'
                  )}
                >
                  {isAnnual && (
                    <div className="absolute -top-3 right-6 rounded-full bg-primary px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-black shadow-md">
                      Best Value
                    </div>
                  )}

                  <div className="flex w-full items-center justify-between">
                    <span className="text-lg font-bold text-white">{plan.Name}</span>
                    <div
                      className={cn(
                        'flex size-5 items-center justify-center rounded-full border transition-all',
                        isSelected
                          ? 'border-primary bg-primary text-black'
                          : 'border-white/30 bg-transparent'
                      )}
                    >
                      {isSelected && <Icon icon="ph:check-bold" className="size-3 stroke-[3]" />}
                    </div>
                  </div>

                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-3xl font-extrabold text-white">
                      ₦{plan.PriceNaira?.toLocaleString()}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      / {isAnnual ? 'year' : 'month'}
                    </span>
                  </div>

                  {isAnnual && (
                    <span className="mt-1 text-xs text-primary font-medium">
                      ≈ ₦{perMonth?.toLocaleString()}/month
                    </span>
                  )}

                  {plan.Description && (
                    <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                      {plan.Description}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Payment Form & Checkout */}
        <div className="mt-12 mx-auto max-w-xl rounded-2xl border border-white/10 bg-[#120e1d] p-6 sm:p-8 backdrop-blur-md shadow-2xl">
          <h3 className="text-lg font-bold text-white display">Payment Information</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Your receipt and confirmation will be sent to this email address.
          </p>

          <div className="mt-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-muted-foreground focus:border-primary focus:bg-white/10 focus:outline-none transition"
              />
            </div>

            <Button
              onClick={handleSubscribe}
              disabled={initiateMutation.isPending || !selectedPlanId}
              className="brand-sweep w-full h-12 rounded-full text-base font-semibold text-white shadow-xl hover:opacity-95"
            >
              {initiateMutation.isPending ? 'Redirecting to Paystack…' : 'Subscribe with Paystack'}
            </Button>

            <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
              <Icon icon="ph:shield-check-fill" className="size-4 text-emerald-400" />
              <span>Secured by Paystack • Cards, Bank Transfer, & USSD supported</span>
            </div>
          </div>
        </div>

        {/* Feature Highlights */}
        <div className="mt-16 border-t border-white/8 pt-12">
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-center text-white display">
            Everything included in your subscription
          </h3>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PERKS.map((perk) => (
              <div
                key={perk.title}
                className="flex items-start gap-4 rounded-2xl border border-white/8 bg-white/[0.02] p-5 backdrop-blur-sm"
              >
                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon icon={perk.icon} className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white">{perk.title}</h4>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {perk.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
